import React from 'react';
import PropTypes from 'prop-types';
import {
    CardNumberElement,
    CardExpiryElement,
    CardCVCElement
} from 'react-stripe-elements';
import { Field } from 'redux-form';
import InputGroup from '../../InputGroup';
import TwoGroup from '../../TwoGroup';
import cardHelper from 'creditcards/card';
import { copyFor } from '../../../copy/copy';
import { PaymentMethod } from '../../../utils/enums';

const parseExpDate = value => {
    const digitsOnly = value.replace(/\D/g, '');
    return digitsOnly == null || digitsOnly === '' ? null : digitsOnly.replace(/\D/g, '');
};

const setCarotPosition = ev => {
    setTimeout(() => {
        var target = ev.target;
        var value = target.value;
        target.selectionStart = target.selectionEnd = value.length;
    }, 50);
};

const formatExpDate = (value, unParsedPrevValue) => {
    let newValue = value;

    if (value && value.length > 1) {
        newValue = `${value.slice(0, 2)}`;
        if (value.length === 2 && unParsedPrevValue && unParsedPrevValue.endsWith(' / '))
            return newValue;

        newValue += ' / ' + value.slice(2);
    }

    if (newValue && newValue.length === 9) {
        return newValue.slice(0, 5) + newValue.slice(-2);
    }

    return newValue;
};

const CreditCardInput = props => (
    <Field
        name="cardNumber"
        className={'fs-block'}
        onBlur={() => props.checkForBlurError('cardNumber')}
        submissionError={props.errors.includes('cardNumber')}
        clearSubmitErrors={props.clearSubmitErrors}
        component={InputGroup}
        customInput={props.customInput}
        normalize={props.customInput ? null : value => cardHelper.format(value)}
        parse={props.customInput ? null : value => cardHelper.parse(value)}
        type="tel"
        inputmode="numeric"
        label="Credit card number"
        placeholder="Credit Card Number"
        autocomplete="cc-number"
        onChange={props.customInput ? null : ev => setCarotPosition(ev)}
        onValidityChange={props.onValidityChange}
    />
);

CreditCardInput.propTypes = {
    customInput: PropTypes.func,
    onValidityChange: PropTypes.func,
    errors: PropTypes.array,
    clearSubmitErrors: PropTypes.func,
    checkForBlurError: PropTypes.func
};

const CvvInput = props => (
    <Field
        name="cvv"
        className={'fs-block'}
        onBlur={() => props.checkForBlurError('cvv')}
        submissionError={props.errors.includes('cvv')}
        clearSubmitErrors={props.clearSubmitErrors}
        component={InputGroup}
        customInput={props.customInput}
        type="tel"
        inputmode="numeric"
        label="CVV"
        autocomplete="cc-csc"
        placeholder="CVV"
        maxLength={4}
        onValidityChange={props.onValidityChange}
    />
);

CvvInput.propTypes = {
    customInput: PropTypes.func,
    onValidityChange: PropTypes.func,
    errors: PropTypes.array,
    clearSubmitErrors: PropTypes.func,
    checkForBlurError: PropTypes.func
};

const ExpirationDateInput = props => (
    <Field
        name="expDate"
        className={'fs-block'}
        onBlur={() => props.checkForBlurError('expDate')}
        submissionError={props.errors.includes('expDate')}
        clearSubmitErrors={props.clearSubmitErrors}
        component={InputGroup}
        customInput={props.customInput}
        label="Exp. Date"
        normalize={
            props.customInput
                ? null
                : (value, unParsedPrevValue) => formatExpDate(value, unParsedPrevValue)
        }
        parse={props.customInput ? null : value => parseExpDate(value)}
        onChange={ev => setCarotPosition(ev)}
        maxLength={7}
        placeholder="Exp. Date"
        type="tel"
        autocomplete="cc-exp"
        onValidityChange={props.onValidityChange}
    />
);

ExpirationDateInput.propTypes = {
    customInput: PropTypes.func,
    onValidityChange: PropTypes.func,
    errors: PropTypes.array,
    clearSubmitErrors: PropTypes.func,
    checkForBlurError: PropTypes.func
};

const PostalCodeInput = props => (
    <Field
        name="postalCode"
        className={'fs-block'}
        onBlur={() => props.checkForBlurError('postalCode')}
        submissionError={props.errors.includes('postalCode')}
        clearSubmitErrors={props.clearSubmitErrors}
        component={InputGroup}
        type="text"
        label={copyFor('formFields', 'postalCode')}
        placeholder={copyFor('placeholders', 'postalCode')}
        autocomplete="billing postal-code"
        maxLength={10}
    />
);

PostalCodeInput.propTypes = {
    errors: PropTypes.array,
    clearSubmitErrors: PropTypes.func,
    checkForBlurError: PropTypes.func
};

class OrgHunterCreditCardInputs extends React.Component {
    render() {
        return (
            <div>
                <CreditCardInput
                    checkForBlurError={this.props.checkForBlurError}
                    errors={this.props.errors}
                    clearSubmitErrors={this.props.clearSubmitErrors}
                    customInput={CardNumberElement}
                    onValidityChange={isValid =>
                        this.props.onOrgHunterCcValidityChange('cardNumber', isValid)
                    }
                />
                <TwoGroup>
                    <ExpirationDateInput
                        checkForBlurError={this.props.checkForBlurError}
                        errors={this.props.errors}
                        clearSubmitErrors={this.props.clearSubmitErrors}
                        customInput={CardExpiryElement}
                        onValidityChange={isValid =>
                            this.props.onOrgHunterCcValidityChange('expDate', isValid)
                        }
                    />
                    <CvvInput
                        checkForBlurError={this.props.checkForBlurError}
                        errors={this.props.errors}
                        clearSubmitErrors={this.props.clearSubmitErrors}
                        customInput={CardCVCElement}
                        onValidityChange={isValid =>
                            this.props.onOrgHunterCcValidityChange('cvv', isValid)
                        }
                    />
                    <PostalCodeInput
                        checkForBlurError={this.props.checkForBlurError}
                        errors={this.props.errors}
                        clearSubmitErrors={this.props.clearSubmitErrors}
                    />
                </TwoGroup>
            </div>
        );
    }
}

OrgHunterCreditCardInputs.propTypes = {
    onOrgHunterCcValidityChange: PropTypes.func,
    errors: PropTypes.array,
    clearSubmitErrors: PropTypes.func,
    checkForBlurError: PropTypes.func
};

const RegularCreditCardInputs = props => (
    <div>
        <CreditCardInput
            checkForBlurError={props.checkForBlurError}
            errors={props.errors}
            clearSubmitErrors={props.clearSubmitErrors}
        />
        <TwoGroup>
            <ExpirationDateInput
                checkForBlurError={props.checkForBlurError}
                errors={props.errors}
                clearSubmitErrors={props.clearSubmitErrors}
            />
            <CvvInput
                checkForBlurError={props.checkForBlurError}
                errors={props.errors} 
                clearSubmitErrors={props.clearSubmitErrors} />
            <PostalCodeInput
                checkForBlurError={props.checkForBlurError}
                errors={props.errors}
                clearSubmitErrors={props.clearSubmitErrors}
            />
        </TwoGroup>
    </div>
);

RegularCreditCardInputs.propTypes = {
    errors: PropTypes.array.isRequired,
    clearSubmitErrors: PropTypes.func.isRequired,
    checkForBlurError: PropTypes.func
};

const CreditCardInputs = props => {
    return props.paymentMethod === PaymentMethod.OrgHunter ? (
        <OrgHunterCreditCardInputs {...props} />
    ) : (
        <RegularCreditCardInputs {...props} />
    );
};

CreditCardInputs.propTypes = {
    paymentMethod: PropTypes.string.isRequired,
    onValidityChange: PropTypes.func
};

export default CreditCardInputs;
